import { memo, useCallback, useMemo, useState } from 'react';
import { Button } from '../../components/Button';
import { useEntryDescription } from '../../components/CreditLedger/LedgerRow';
import { Modal } from '../../components/Modal';
import { ConfirmDialog } from '../../components/Modal/ConfirmDialog';
import { useFormatDate } from '../../hooks/useFormatDate';
import useOpen from '../../hooks/useOpen';
import { ToastType, useToastNotifications } from '../../hooks/useToastNotifications';
import { T, useT } from '../../translation/src';
import { CreditLedgerEntryFieldsFragment, useSuperadminLiveGrantsQuery, useSuperadminRemoveCreditsMutation } from '../../types/graphqlTypes';

// The balance shown with the organisation comes from the organisations list. The grants themselves need no
// refetch: the mutation returns the grant it changed, and an emptied one is left out below.
const REFETCH_QUERIES = ['superadminOrganisations'];

interface GrantRowProps {
  entry: CreditLedgerEntryFieldsFragment;
  busy: boolean;
  onRemove: (entry: CreditLedgerEntryFieldsFragment, amount: number) => void;
  onRemoveAll: (entry: CreditLedgerEntryFieldsFragment) => void;
}
const GrantRow: React.FC<GrantRowProps> = ({ entry, busy, onRemove, onRemoveAll }) => {
  const { formatDay } = useFormatDate();
  const description = useEntryDescription(entry);
  const remaining = entry.remaining ?? 0;
  return (
    <div className="flex items-center gap-3 border-b border-gray-200 py-2 text-sm last:border-b-0 dark:border-gray-700">
      <span className="w-16 shrink-0 rounded-full bg-neon-green-100 px-2 py-0.5 text-center font-mono text-xs font-medium text-neon-green-800 dark:bg-neon-green-900 dark:text-neon-green-200">
        {remaining} / {entry.amount}
      </span>
      <div className="flex min-w-0 grow flex-col">
        <span className="truncate text-gray-700 dark:text-gray-300">{description}</span>
        <span className="text-xs text-gray-500">
          {formatDay(new Date(entry.createdAt))} ·{' '}
          {entry.expiresAt ? (
            <>
              <T _str="expires" />: {formatDay(new Date(entry.expiresAt))}
            </>
          ) : (
            <T _str="does not expire" />
          )}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button variant="tertiary" size="xs" disabled={busy} onClick={() => onRemove(entry, 1)}>
          −1
        </Button>
        <Button variant="tertiary" size="xs" disabled={busy} onClick={() => onRemoveAll(entry)}>
          <T _str="remove all" swc />
        </Button>
      </div>
    </div>
  );
};

export interface RemoveCreditsModalProps {
  onClose: () => void;
  open?: boolean;
  organisationId: string;
}
// Every grant of the organisation that still holds projects, with when it expires, to take projects off
// one at a time or a whole grant at once: granted by mistake, refunded, ...
const RemoveCreditsModal_: React.FC2<RemoveCreditsModalProps> = ({ onClose, open, organisationId }) => {
  const { data, refetch } = useSuperadminLiveGrantsQuery({ variables: { organisationId }, skip: !open, fetchPolicy: 'cache-and-network' });
  const [removeCredits] = useSuperadminRemoveCreditsMutation({ refetchQueries: REFETCH_QUERIES });
  const [removingId, setRemovingId] = useState<string | null>(null);
  const { open: confirmOpen, value: confirmEntry, onOpenWithValue: openConfirm, onClose: closeConfirm } = useOpen<CreditLedgerEntryFieldsFragment>();
  const { showToast } = useToastNotifications();
  const failed = useT('could not remove the projects', { swc: true });

  // A grant emptied here stays in the cached list with nothing left: it is done with.
  const grants = useMemo(() => (data?.superadminLiveGrants || []).filter((entry) => (entry.remaining ?? 0) > 0), [data]);
  const total = useMemo(() => grants.reduce((sum, entry) => sum + (entry.remaining ?? 0), 0), [grants]);

  const onRemove = useCallback(
    async (entry: CreditLedgerEntryFieldsFragment, amount: number) => {
      setRemovingId(entry.id);
      try {
        await removeCredits({ variables: { entryId: entry.id, amount } });
      } catch (e) {
        showToast({ title: failed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
        // Refused because the grant holds fewer than shown (spent meanwhile): show what it holds now.
        void refetch().catch(() => undefined);
      } finally {
        setRemovingId(null);
      }
    },
    [failed, refetch, removeCredits, showToast],
  );

  const onConfirmRemoveAll = useCallback(() => {
    if (confirmEntry) void onRemove(confirmEntry, confirmEntry.remaining ?? 0);
  }, [confirmEntry, onRemove]);

  return (
    <>
      <Modal.Container open={open} onClose={onClose} panelClassName="w-full max-w-lg">
        <Modal.Header onClose={onClose}>
          <T _str="remove projects" swc />
        </Modal.Header>
        <Modal.Body className="flex flex-col gap-3 px-1">
          <span className="text-sm text-gray-500">
            <T _str="{total} projects left, spent from top to bottom. Removing them does not change any invoice." args={{ total }} />
          </span>
          {/* Room for the scrollbar, so the buttons are not under it. */}
          <div className="flex max-h-96 min-h-40 flex-col overflow-y-auto py-1 pr-3">
            {grants.length === 0 && (
              <div className="flex grow items-center justify-center text-sm text-gray-500">
                <T _str="no projects left to remove" swc />
              </div>
            )}
            {grants.map((entry) => (
              <GrantRow key={entry.id} entry={entry} busy={removingId === entry.id} onRemove={onRemove} onRemoveAll={openConfirm} />
            ))}
          </div>
        </Modal.Body>
      </Modal.Container>
      <ConfirmDialog
        open={confirmOpen}
        onClose={closeConfirm}
        onConfirm={onConfirmRemoveAll}
        title={<T _str="remove all projects of this grant?" swc />}
        message={<T _str="{amount} projects will be taken off the balance. This cannot be undone, only granted again." args={{ amount: confirmEntry?.remaining ?? 0 }} />}
        confirmLabel={<T _str="remove all" swc />}
      />
    </>
  );
};

export const RemoveCreditsModal = memo(RemoveCreditsModal_);
